import { domainLabel } from '@/features/common/useThematicDomains';
import type { DomainChip } from '@/features/publicsite/types/registry/DomainChip';
import type { InstitutionBlocks } from '@/features/publicsite/types/training/InstitutionBlocks';
import type { TrainingInstitution } from '@/features/publicsite/types/training/TrainingInstitution';

/** The institution's domains as the reader's language names them, in the stored order. */
export function institutionChips(
  institution: Pick<TrainingInstitution, 'specializationDomainLabels'>,
  language: string,
): DomainChip[] {
  return (institution.specializationDomainLabels ?? []).map((domain) => ({
    id: domain.id,
    label: domainLabel(domain, language),
  }));
}

/** Those same domains as one line under the name on a card, or null when there are none. */
export function institutionSubtitle(
  institution: Pick<TrainingInstitution, 'specializationDomainLabels'>,
  language: string,
): string | null {
  const names = institutionChips(institution, language).map((chip) => chip.label);
  return names.length > 0 ? names.join(', ') : null;
}

/** The contact as one line — either half may be missing, and both may be. */
export function institutionContactLine(institution: Pick<TrainingInstitution, 'contact'>): string {
  return [institution.contact?.contactPerson, institution.contact?.email]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Which blocks the profile page draws.
 *
 * Every one is conditional, and that is the point rather than tidiness: an institution approved
 * before V092 carries none of these fields, and a heading over an empty paragraph would read as
 * a profile that failed to load instead of one that was never filled in. The contact block
 * appears for a website alone, since the website is shown inside it.
 *
 * The map link follows AdvisorCard's rule: a point exists per served raion, so an institution
 * with none has nothing on the map and the link would land on a search matching nothing.
 */
export function institutionBlocks(institution: TrainingInstitution): InstitutionBlocks {
  return {
    domains: (institution.specializationDomainLabels ?? []).length > 0,
    overview: Boolean(institution.overview),
    teachingStaff: Boolean(institution.teachingStaff),
    infrastructure: Boolean(institution.infrastructure),
    contact: Boolean(institutionContactLine(institution) || institution.website),
    zones: (institution.zoneNames ?? []).length > 0,
    programs: (institution.programs ?? []).length > 0,
    map: (institution.zoneNames ?? []).length > 0,
  };
}
